import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarClock,
  CreditCard,
  Sparkles,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  HardDrive,
  Check,
} from 'lucide-react';
import { billingApi, type ApiPlan, type BillingTerm } from '../../api';
import { setSubscriptionCache } from '../../lib/subscription';
import { setTenantSession } from '../../lib/tenant';
import { planItemLabels } from '../../lib/planEntitlements';
import {
  defaultBillingTerm,
  formatPlanPrice,
  getPricingOptions,
  perMonthEquivalent,
  resolvePlanPrice,
  TERM_LABELS,
} from '../../lib/planPricing';
import { PlanDurationPicker } from '../common/PlanDurationPicker';
import { openRazorpayCheckout } from '../../lib/razorpay';

function planRank(plan: ApiPlan) {
  const from = getPricingOptions(plan)[0]?.priceInr ?? plan.priceInr;
  return plan.sortOrder * 100000 + from;
}

function actionLabel(
  action: 'upgrade' | 'downgrade' | 'renew',
  price: number | null,
  term: BillingTerm,
  short = false
) {
  const priceStr = price != null ? formatPlanPrice(price) : '';
  const termStr = TERM_LABELS[term];
  if (short) {
    if (action === 'upgrade') return priceStr ? `Upgrade · ${priceStr}` : 'Upgrade';
    if (action === 'downgrade') return priceStr ? `Downgrade · ${priceStr}` : 'Downgrade';
    return priceStr ? `Renew · ${priceStr}` : 'Renew';
  }
  if (action === 'upgrade') return priceStr ? `Upgrade · ${priceStr} for ${termStr}` : 'Upgrade';
  if (action === 'downgrade') return priceStr ? `Downgrade · ${priceStr} for ${termStr}` : 'Downgrade';
  return priceStr ? `Renew · ${priceStr} for ${termStr}` : 'Renew';
}

export default function SubscriptionSettings() {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<ApiPlan[]>([]);
  const [status, setStatus] = useState<Awaited<ReturnType<typeof billingApi.status>> | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [autoPay, setAutoPay] = useState(true);
  const [termByPlan, setTermByPlan] = useState<Record<string, BillingTerm>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [planRes, statusRes] = await Promise.all([billingApi.listPlans(), billingApi.status()]);
      setPlans(planRes.plans);
      setStatus(statusRes);
      setSubscriptionCache(statusRes.subscription);
      if (statusRes.organization) {
        setTenantSession({
          id: statusRes.organization.id,
          name: statusRes.organization.name,
          slug: statusRes.organization.slug,
          planId: statusRes.organization.planId,
          subscriptionStatus: statusRes.organization.subscriptionStatus,
        });
      }
      const terms: Record<string, BillingTerm> = {};
      planRes.plans.forEach((p) => {
        terms[p.planId] =
          statusRes.subscription?.planId === p.planId && statusRes.subscription?.billingTerm
            ? statusRes.subscription.billingTerm
            : defaultBillingTerm(p);
      });
      setTermByPlan(terms);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load subscription.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const currentPlan = useMemo(
    () => plans.find((p) => p.planId === status?.planId) || status?.subscription?.plan || null,
    [plans, status]
  );

  const currentTerm = status?.subscription?.billingTerm || (currentPlan ? defaultBillingTerm(currentPlan) : 'monthly');

  const payForPlan = async (planId: string, action: 'renew' | 'upgrade' | 'downgrade', billingTerm: BillingTerm) => {
    setBusy(`${action}-${planId}-${billingTerm}`);
    setError('');
    try {
      const session = await billingApi.subscriptionOrder({ planId, action, billingTerm, autoPay });
      if (session.skipPayment) {
        setSubscriptionCache(session.subscription);
        await load();
        return;
      }
      const result = await openRazorpayCheckout({
        keyId: session.razorpayKeyId,
        orderId: session.order.id,
        amount: session.order.amount,
        currency: session.order.currency,
        name: 'Edge Finance Pro',
        description: `${action} — ${session.plan.name} (${TERM_LABELS[billingTerm]})`,
        prefill: {
          email: status?.organization?.adminEmail || localStorage.getItem('userEmail') || undefined,
          name: status?.organization?.name,
        },
        paymentMode: session.paymentMode,
        demo: session.order.demo,
        notes: { action, planId, billingTerm },
      });
      if (!result) return;
      const verified = await billingApi.verifySubscriptionPayment({
        paymentId: session.paymentId,
        razorpay_order_id: result.orderId,
        razorpay_payment_id: result.paymentId,
        razorpay_signature: result.signature,
      });
      setSubscriptionCache(verified.subscription);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Payment failed.');
    } finally {
      setBusy('');
    }
  };

  const sub = status?.subscription;
  const currentRank = currentPlan ? planRank(currentPlan) : 0;
  const renewTerm = currentPlan ? termByPlan[currentPlan.planId] || currentTerm : 'monthly';
  const renewPrice = currentPlan ? resolvePlanPrice(currentPlan, renewTerm) : null;

  return (
    <div className="space-y-4 sm:space-y-8">
      {error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900 sm:rounded-xl sm:px-4 sm:py-3 sm:text-sm">{error}</div>
      ) : null}

      {/* Current plan hero */}
      <section className="overflow-hidden rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white shadow-sm sm:rounded-2xl">
        <div className="border-b border-emerald-100/80 px-3 py-2.5 sm:px-6 sm:py-4">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700 sm:text-xs">Your subscription</p>
        </div>

        <div className="p-3 sm:p-6">
          {loading ? (
            <p className="text-xs text-zinc-500 sm:text-sm">Loading subscription…</p>
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-6">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                  <h3 className="text-base font-bold tracking-tight text-zinc-900 sm:text-xl">
                    {sub?.plan?.name || status?.planId || 'No plan'}
                  </h3>
                  {sub?.isTrial ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-800 sm:px-2.5 sm:text-xs">
                      <Sparkles className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> Free trial
                    </span>
                  ) : null}
                </div>

                {sub?.isTrial ? (
                  <p className="mt-2 rounded-lg border border-violet-200 bg-violet-50/80 px-3 py-2 text-[11px] leading-relaxed text-violet-900 sm:text-xs">
                    You are on a free trial. Upgrade or renew below anytime — your workspace, customers, and ledger data stay on the same account.
                  </p>
                ) : null}

                {sub?.termPriceInr != null ? (
                  <div className="mt-1.5 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 sm:mt-2 sm:gap-x-2 sm:gap-y-1">
                    <span className="text-lg font-bold tracking-tight text-emerald-800 sm:text-3xl">
                      {formatPlanPrice(sub.termPriceInr)}
                    </span>
                    {sub.billingTermLabel ? (
                      <span className="text-xs font-medium text-zinc-500 sm:text-sm">for {sub.billingTermLabel}</span>
                    ) : null}
                    {sub.billingTerm && sub.billingTerm !== 'monthly' && sub.termPriceInr ? (
                      <span className="text-[11px] text-zinc-400 sm:text-sm">
                        ({formatPlanPrice(perMonthEquivalent(sub.termPriceInr, sub.billingTerm === 'yearly' ? 12 : 3))}/mo)
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {sub?.expiresLabel ? (
                <div className="w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 shadow-sm sm:w-auto sm:rounded-xl sm:px-4 sm:py-3">
                  <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-500 sm:gap-1.5 sm:text-xs">
                    <CalendarClock className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> Renewal date
                  </p>
                  <p className={`mt-0.5 text-xs font-semibold sm:mt-1 sm:text-sm ${sub.isExpired ? 'text-rose-700' : 'text-emerald-800'}`}>
                    {sub.expiresLabel}
                  </p>
                </div>
              ) : null}
            </div>
          )}

          {!loading && (sub?.mediaStorageLimitMb != null || sub?.databaseStorageLimitMb != null || sub?.storageLimitMb != null) ? (
            <div className="mt-3 space-y-2.5 rounded-lg border border-zinc-200 bg-zinc-50/80 p-3 sm:mt-6 sm:space-y-3 sm:rounded-xl sm:p-4">
              <p className="text-xs font-semibold text-zinc-800 sm:text-sm">Storage usage</p>

              {(sub?.mediaStorageLimitMb ?? sub?.storageLimitMb) != null ? (
                <div>
                  <div className="flex items-center justify-between gap-2 text-xs sm:text-sm">
                    <span className="inline-flex min-w-0 items-center gap-1 font-medium text-zinc-700 sm:gap-1.5">
                      <HardDrive className="h-3.5 w-3.5 shrink-0 text-zinc-400 sm:h-4 sm:w-4" />
                      <span className="truncate">Media</span>
                    </span>
                    <span className="shrink-0 text-[11px] text-zinc-600 sm:text-sm">
                      <span className="font-semibold text-zinc-900">{sub.mediaStorageUsedMb ?? sub.storageUsedMb ?? 0}</span>
                      <span className="text-zinc-400"> / {sub.mediaStorageLimitMb ?? sub.storageLimitMb} MB</span>
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-zinc-200 sm:mt-2 sm:h-2">
                    <div
                      className={`h-full rounded-full transition-all ${sub.mediaStorageFull ?? sub.storageFull ? 'bg-rose-500' : 'bg-emerald-600'}`}
                      style={{ width: `${Math.min(100, sub.mediaStoragePercentUsed ?? sub.storagePercentUsed ?? 0)}%` }}
                    />
                  </div>
                </div>
              ) : null}

              {sub?.databaseStorageLimitMb != null ? (
                <div>
                  <div className="flex items-center justify-between gap-2 text-xs sm:text-sm">
                    <span className="inline-flex min-w-0 items-center gap-1 font-medium text-zinc-700 sm:gap-1.5">
                      <HardDrive className="h-3.5 w-3.5 shrink-0 text-violet-400 sm:h-4 sm:w-4" />
                      <span className="truncate">Database</span>
                    </span>
                    <span className="shrink-0 text-[11px] text-zinc-600 sm:text-sm">
                      <span className="font-semibold text-zinc-900">{sub.databaseStorageUsedMb ?? 0}</span>
                      <span className="text-zinc-400"> / {sub.databaseStorageLimitMb} MB</span>
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-zinc-200 sm:mt-2 sm:h-2">
                    <div
                      className={`h-full rounded-full transition-all ${sub.databaseStorageFull ? 'bg-rose-500' : 'bg-violet-600'}`}
                      style={{ width: `${Math.min(100, sub.databaseStoragePercentUsed ?? 0)}%` }}
                    />
                  </div>
                </div>
              ) : null}

              {(sub?.mediaStorageFull ?? sub.storageFull) || sub?.databaseStorageFull ? (
                <p className="text-[10px] text-rose-700 sm:text-xs">Storage limit reached — upgrade or remove data.</p>
              ) : null}
            </div>
          ) : null}

          {!loading && currentPlan ? (
            <div className="mt-3 space-y-3 border-t border-emerald-100 pt-3 sm:mt-6 sm:space-y-4 sm:pt-6">
              <div>
                <p className="text-xs font-semibold text-zinc-900 sm:text-sm">Extend your current plan</p>
                <p className="mt-0.5 text-[10px] text-zinc-500 sm:text-xs">Pick duration — price updates instantly.</p>
              </div>
              <PlanDurationPicker
                plan={currentPlan}
                value={renewTerm}
                onChange={(t) => setTermByPlan((s) => ({ ...s, [currentPlan.planId]: t }))}
                variant="toggle"
              />
              <button
                type="button"
                disabled={!!busy || renewPrice == null}
                onClick={() => void payForPlan(currentPlan.planId, sub?.isTrial ? 'upgrade' : 'renew', renewTerm)}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-800 disabled:opacity-60 sm:gap-2 sm:rounded-xl sm:px-4 sm:py-3 sm:text-sm sm:w-auto"
              >
                <RefreshCw className={`h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4 ${busy.startsWith(`${sub?.isTrial ? 'upgrade' : 'renew'}-${currentPlan.planId}`) ? 'animate-spin' : ''}`} />
                <span className="truncate sm:hidden">
                  {busy.startsWith(`upgrade-${currentPlan.planId}`) || busy.startsWith(`renew-${currentPlan.planId}`)
                    ? 'Processing…'
                    : sub?.isTrial
                      ? renewPrice != null
                        ? `Upgrade · ${formatPlanPrice(renewPrice)}`
                        : 'Upgrade to paid'
                      : actionLabel('renew', renewPrice, renewTerm, true)}
                </span>
                <span className="hidden truncate sm:inline">
                  {busy.startsWith(`upgrade-${currentPlan.planId}`) || busy.startsWith(`renew-${currentPlan.planId}`)
                    ? 'Processing…'
                    : sub?.isTrial
                      ? renewPrice != null
                        ? `Upgrade to paid · ${formatPlanPrice(renewPrice)} for ${TERM_LABELS[renewTerm]}`
                        : 'Upgrade to paid plan'
                      : actionLabel('renew', renewPrice, renewTerm, false)}
                </span>
              </button>
            </div>
          ) : null}
        </div>
      </section>

      <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-xs text-zinc-700 shadow-sm sm:gap-2.5 sm:rounded-xl sm:px-4 sm:py-3 sm:text-sm">
        <input
          type="checkbox"
          className="h-3.5 w-3.5 shrink-0 rounded border-zinc-300 text-emerald-600 sm:h-4 sm:w-4"
          checked={autoPay}
          onChange={(e) => setAutoPay(e.target.checked)}
        />
        <span className="min-w-0">
          <span className="font-medium">Auto-pay renewals</span>
          <span className="mt-0.5 block text-[10px] text-zinc-500 sm:text-xs">Charges automatically when your plan expires.</span>
        </span>
      </label>

      {/* Change plan */}
      <section>
        <div className="mb-3 sm:mb-4">
          <h3 className="text-sm font-semibold text-zinc-900 sm:text-base">Switch plan</h3>
          <p className="mt-0.5 text-[11px] text-zinc-500 sm:mt-1 sm:text-sm">Compare tiers, pick duration, then upgrade or downgrade.</p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:gap-5 md:grid-cols-2 xl:grid-cols-3">
          {plans.map((plan) => {
            const isCurrent = status?.planId === plan.planId && status?.subscriptionStatus === 'active';
            const term = termByPlan[plan.planId] || defaultBillingTerm(plan);
            const rank = planRank(plan);
            const action: 'upgrade' | 'downgrade' | 'renew' = isCurrent
              ? 'renew'
              : rank > currentRank
                ? 'upgrade'
                : rank < currentRank
                  ? 'downgrade'
                  : 'renew';
            const price = resolvePlanPrice(plan, term);
            const labels = planItemLabels(plan).slice(0, 4);
            const busyKey = `${sub?.isTrial && isCurrent ? 'upgrade' : isCurrent ? 'renew' : action}-${plan.planId}-${term}`;
            const opt = getPricingOptions(plan).find((o) => o.term === term);

            return (
              <article
                key={plan.planId}
                className={`flex flex-col rounded-xl border p-3 transition-shadow sm:rounded-2xl sm:border-2 sm:p-5 ${
                  isCurrent
                    ? 'border-emerald-400 bg-gradient-to-b from-emerald-50/60 to-white shadow-sm sm:shadow-md sm:shadow-emerald-500/10'
                    : 'border-zinc-200 bg-white shadow-sm hover:shadow-md'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h4 className="text-sm font-bold text-zinc-900 sm:text-lg">{plan.name}</h4>
                    {plan.description ? (
                      <p className="mt-0.5 text-[10px] leading-snug text-zinc-500 line-clamp-2 sm:text-xs">{plan.description}</p>
                    ) : null}
                  </div>
                  {isCurrent ? (
                    <span className="shrink-0 rounded-full bg-emerald-600 px-2 py-px text-[9px] font-bold uppercase tracking-wide text-white sm:px-2.5 sm:py-0.5 sm:text-[10px]">
                      Current
                    </span>
                  ) : null}
                </div>

                {price != null && opt ? (
                  <div className="mt-2 rounded-lg bg-zinc-50 px-2.5 py-2 sm:mt-4 sm:px-3 sm:py-2.5">
                    <p className="text-lg font-bold tracking-tight text-zinc-900 sm:text-3xl">{formatPlanPrice(price)}</p>
                    <p className="mt-0.5 text-[11px] text-zinc-500 sm:text-sm">
                      for {opt.label}
                      {opt.months > 1 ? (
                        <span className="text-zinc-400"> · {formatPlanPrice(perMonthEquivalent(price, opt.months))}/mo</span>
                      ) : null}
                    </p>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-zinc-400 sm:mt-3 sm:text-sm">Select a duration below</p>
                )}

                <p className="mt-2.5 text-xs font-semibold text-zinc-700 sm:mt-4 sm:text-sm">Choose duration</p>
                <div className="mt-1.5 sm:mt-2">
                  <PlanDurationPicker
                    plan={plan}
                    value={term}
                    onChange={(t) => setTermByPlan((s) => ({ ...s, [plan.planId]: t }))}
                    variant="toggle"
                  />
                </div>

                <ul className="mt-2.5 flex-1 space-y-1 sm:mt-4 sm:space-y-1.5">
                  {labels.map((l) => (
                    <li key={l} className="flex items-start gap-1.5 text-[10px] text-zinc-600 sm:gap-2 sm:text-xs">
                      <Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600 sm:h-3.5 sm:w-3.5" />
                      {l}
                    </li>
                  ))}
                </ul>

                {(plan.mediaStorageLimitMb ?? plan.storageLimitMb) || plan.databaseStorageLimitMb ? (
                  <p className="mt-2 text-[10px] text-zinc-400 sm:mt-3 sm:text-[11px]">
                    {plan.mediaStorageLimitMb ?? plan.storageLimitMb
                      ? `${plan.mediaStorageLimitMb ?? plan.storageLimitMb} MB media`
                      : ''}
                    {(plan.mediaStorageLimitMb ?? plan.storageLimitMb) && plan.databaseStorageLimitMb ? ' · ' : ''}
                    {plan.databaseStorageLimitMb ? `${plan.databaseStorageLimitMb} MB database` : ''}
                  </p>
                ) : null}

                <button
                  type="button"
                  disabled={!!busy || price == null}
                  onClick={() =>
                    void payForPlan(
                      plan.planId,
                      sub?.isTrial && isCurrent ? 'upgrade' : isCurrent ? 'renew' : action,
                      term
                    )
                  }
                  className={`mt-2.5 inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold transition disabled:opacity-50 sm:mt-5 sm:gap-2 sm:rounded-xl sm:px-4 sm:py-3 sm:text-sm ${
                    isCurrent
                      ? 'border border-emerald-600 bg-white text-emerald-800 hover:bg-emerald-50'
                      : action === 'upgrade'
                        ? 'bg-emerald-700 text-white shadow-sm hover:bg-emerald-800'
                        : 'border border-zinc-300 bg-white text-zinc-800 hover:bg-zinc-50'
                  }`}
                >
                  {action === 'upgrade' ? (
                    <ArrowUp className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                  ) : action === 'downgrade' ? (
                    <ArrowDown className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                  ) : (
                    <CreditCard className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                  )}
                  <span className="truncate sm:hidden">
                    {busy === busyKey ? 'Processing…' : actionLabel(isCurrent ? 'renew' : action, price, term, true)}
                  </span>
                  <span className="hidden truncate sm:inline">
                    {busy === busyKey ? 'Processing…' : actionLabel(isCurrent ? 'renew' : action, price, term, false)}
                  </span>
                </button>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
